import { useLiveQuery } from '@sanity/preview-kit';
import { useEffect, useState } from 'react';

import { slugToMetalPrice } from '@core/constant';
import { useContextMediaQuery, useContextRouting } from '@core/context';
import { useLanguage } from '@core/hook';
import { useMetalIso } from '@core/provider';
import { applyAbTest, getSanityQuery } from '@core/sanity/utils';
import {
  ECurrency,
  ELiveChartsPeriod,
  EMetal,
  EMetalSlug,
  ERouting,
  WeightUnitType,
} from '@core/type';
import { SanityCharts, SanityPageResponse } from '@core/type/sanity';
import { TableOfContentContainer, WidgetChart } from '@core/widget';

import { PageBuilder } from '../../layout';
import type { ChartPageProps } from './ViewCharts';
import { useChartsCurrencyUpdate } from './use-charts-currency-update';

export const Charts = ({ cms, shouldUseVariation }: ChartPageProps) => {
  const { language } = useLanguage();
  const { query } = useContextRouting();
  const { isPhone } = useContextMediaQuery();
  const [data] = useLiveQuery(cms || null, getSanityQuery(ERouting.CHARTS, query, language));

  const cmsResult = applyAbTest(data as SanityPageResponse, shouldUseVariation) as SanityCharts;
  const [metal, setMetal] = useState<EMetal>(cmsResult?.body?.metal as EMetal);
  const { setMetalIso } = useMetalIso();
  useChartsCurrencyUpdate();

  useEffect(() => {
    setMetalIso(metal);
  }, [metal]);

  useEffect(() => {
    const metal = slugToMetalPrice?.[language]?.[query.metalSlug as EMetalSlug];

    if (metal) {
      setMetal(metal);
    }
  }, [query]);

  return (
    <>
      {cmsResult?.body?.hasLiveChart && (
        <WidgetChart
          metal={metal}
          currency={cmsResult?.body?.currency as ECurrency}
          weightUnit={cmsResult?.body?.weightUnit as WeightUnitType}
          period={cmsResult?.body?.period as ELiveChartsPeriod}
          shouldChangeUrl={
            Object.values(ECurrency).includes(query.slug[0].toUpperCase() as ECurrency) &&
            query.slug.length === 1
          }
          title={cmsResult?.body?.widgetTitle}
          subtitle={cmsResult?.body?.widgetSubtitle}
        />
      )}

      <TableOfContentContainer
        isEnabled={cmsResult?.body?.hasTableOfContent}
        marginTop={isPhone ? '64px' : '104px'}
      >
        <PageBuilder components={cmsResult?.body?.pageBuilder} pageId={cmsResult?._id} />
      </TableOfContentContainer>
    </>
  );
};
